import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, Send, Car } from "lucide-react";
import { HouseChatHostMessage, type HouseChatHostMessageData } from "./HouseChatHostMessage";

interface HouseChat { houseConversationId: number; messages: HouseChatHostMessageData[] }
interface ParkedVehicle { memberId: number; ownerName: string; make: string | null; model: string | null; color: string | null; year: string | null; licensePlate: string | null }

const POLL_MS = 10_000;

// Host-side house chat + parking for a property. Reads/sends the single
// per-property house conversation that members see in their portal, and lists
// every member vehicle for parking. Rendered full-height inside the Messages
// "House Chats" tab; `onRead` fires once the chat loads so the tab can clear the
// property's unread badge (the GET marks it read server-side).
export function HouseChatPanel({ propertyId, onRead }: { propertyId: number; onRead?: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const readFiredRef = useRef(false);

  const chatKey = ["/api/app/properties", propertyId, "house-chat"];
  const { data: chat } = useQuery<HouseChat>({
    queryKey: chatKey,
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/house-chat`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load house chat");
      return res.json();
    },
    refetchInterval: POLL_MS,
  });

  // Once the chat first loads (which marks it read server-side), let the parent
  // refresh the unread badge. Reset when switching properties.
  useEffect(() => {
    readFiredRef.current = false;
  }, [propertyId]);
  useEffect(() => {
    if (chat && !readFiredRef.current) {
      readFiredRef.current = true;
      onRead?.();
    }
  }, [chat, onRead]);

  const { data: vehicles } = useQuery<ParkedVehicle[]>({
    queryKey: ["/api/app/properties", propertyId, "member-vehicles"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/member-vehicles`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load vehicles");
      return res.json();
    },
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [chat]);

  const send = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/app/properties/${propertyId}/house-chat`, { body: draft.trim() }),
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: chatKey });
    },
    onError: (err) => toast({ title: "Could not send", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {/* House chat — fills available height */}
      <div className="flex min-h-0 flex-1 flex-col">
        <h4 className="mb-3 flex shrink-0 items-center gap-2 text-sm font-semibold text-gray-800">
          <MessageSquare className="h-4 w-4 text-brand" /> House Chat
        </h4>
        <div ref={scrollRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg border bg-gray-50 p-3">
          {!chat || chat.messages.length === 0 ? (
            <p className="mt-8 text-center text-sm text-gray-400">No messages yet.</p>
          ) : (
            chat.messages.map((m) => (
              <HouseChatHostMessage
                key={m.id}
                message={m}
                propertyId={propertyId}
                onChanged={() => queryClient.invalidateQueries({ queryKey: chatKey })}
              />
            ))
          )}
        </div>
        <form
          className="mt-2 flex shrink-0 gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) send.mutate();
          }}
        >
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Message the house…" className="text-sm" data-testid="input-house-chat" />
          <Button type="submit" className="bg-brand hover:bg-brand-hover" disabled={send.isPending || !draft.trim()} data-testid="button-send-house-chat">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>

      {/* Parking — stacked below the chat, bounded so it never crowds it out */}
      <div className="shrink-0">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
          <Car className="h-4 w-4 text-brand" /> Parking &amp; Vehicles
        </h4>
        {!vehicles || vehicles.length === 0 ? (
          <p className="rounded-lg border bg-gray-50 p-3 text-sm text-gray-400">No vehicles on file for this house.</p>
        ) : (
          <ul className="max-h-40 space-y-2 overflow-y-auto">
            {vehicles.map((v) => (
              <li key={v.memberId} className="flex items-center justify-between rounded-md border p-2 text-sm">
                <div>
                  <p className="font-medium">{[v.year, v.color, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}</p>
                  <p className="text-xs text-gray-500">{v.ownerName}{v.licensePlate ? ` · ${v.licensePlate}` : ""}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
